import { Server, Response } from 'miragejs';

interface ModuleMockConfig {
    [moduleName: string]: Record<
        string,
        (schema: unknown, request: unknown) => string | object | undefined
    >;
}

let globalMockConfigs: ModuleMockConfig = {};

export const getMockPath = () => {
    return window.location.protocol === 'file:' ? '' : '/mock-api';
};

export const initMockProvider = (
    moduleName: string,
    mockConfig?: Record<string, (schema: unknown, request: unknown) => string | object | undefined>,
    options?: {
        enabled?: boolean;
        responseTiming?: number;
        useAbsolutePath?: boolean;
    },
) => {
    const { enabled = true, responseTiming, useAbsolutePath = false } = options || {};

    if (!enabled) {
        console.log('Mock Server Disabled ✖️');
        return null;
    }

    if (!mockConfig) {
        return window.server || null;
    }

    const isExistingModule = moduleName in globalMockConfigs;
    let isPathConflict = false;

    if (isExistingModule) {
        // To check if any mock path conflict with other module or not
        // e.g. The Holdings module and Invest module both has mock for the same API PATH "/dailybanking/offers"
        Object.entries(globalMockConfigs).forEach(([existingModuleName, existingConfig]) => {
            if (existingModuleName !== moduleName) {
                Object.keys(mockConfig).forEach((path) => {
                    if (path in existingConfig) {
                        console.warn(
                            `Path ${path} already exists in module ${existingModuleName}. Overriding.`,
                        );
                        isPathConflict = true;
                    }
                });
            }
        });
    }

    globalMockConfigs[moduleName] = mockConfig;

    if (window.server && isPathConflict) {
        console.log('Path conflict detected. Replace the conflict route by current mock config...');
        addRoutesToServer(window.server, moduleName, mockConfig);
    }

    if (window.server && !isExistingModule) {
        addRoutesToServer(window.server, moduleName, mockConfig);
        console.log(`Added mocks for the module [${moduleName}] into the mock server ✅`);
        return window.server;
    }

    if (window.server && isExistingModule) {
        return window.server;
    }

    const server = new Server({
        models: {},

        routes() {
            this.passthrough('/menu/*');

            this.namespace = getMockPath();
            this.timing = responseTiming || 1000;

            if (useAbsolutePath) {
                this.urlPrefix = '';
            }

            addRoutesToServer(this, moduleName, mockConfig);
            console.log(`Setup mock server and added mocks for the module [${moduleName}] ✅`);

            this.passthrough('**');
        },
    });

    window.server = server;
    server.logging = true;

    return server;
};

const addRoutesToServer = (
    server: Server,
    moduleName: string,
    mockConfig: Record<string, (schema: unknown, request: unknown) => string | object | undefined>,
) => {
    Object.entries(mockConfig).forEach(([path, mockHandler]) => {
        server.get(path, (schema, request) => {
            try {
                const responseData = mockHandler(schema, request);
                return new Response(
                    200,
                    {
                        'Content-Type': 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Methods': 'GET,HEAD,PUT,PATCH,POST,DELETE',
                        'Access-Control-Allow-Headers': '*',
                    },
                    responseData,
                );
            } catch (error) {
                console.error(`Mock handler error for ${path} in module [${moduleName}]:`, error);
                return new Response(500, {}, { error: 'Mock handler failed' });
            }
        });

        server.post(path, (schema, request) => {
            try {
                const responseData = mockHandler(schema, request);
                return new Response(
                    200,
                    {
                        'Content-Type': 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Methods': 'GET,HEAD,PUT,PATCH,POST,DELETE',
                        'Access-Control-Allow-Headers': '*',
                    },
                    responseData,
                );
            } catch (error) {
                console.error(`Mock handler error for ${path} in module [${moduleName}]:`, error);
                return new Response(500, {}, { error: 'Mock handler failed' });
            }
        });
    });
};

export const clearMockConfigs = () => {
    globalMockConfigs = {};
    if (window.server) {
        window.server.shutdown();
        window.server = null;
    }
};
